import { DOCUMENT } from '@angular/common';
import { Injectable, NgZone, inject, signal } from '@angular/core';

import { revealAll } from './report-reveal.directive';

/**
 * Impression du rapport — c'est-à-dire son export PDF.
 *
 * Il n'y a pas de seconde composition : le PDF est cette page, imprimée. Une
 * génération séparée créerait un deuxième rendu, voué à s'écarter de ce qui a
 * été validé à l'écran.
 *
 * ── Pourquoi une préparation asynchrone ────────────────────────────────────
 * Un `window.print()` direct capture la page telle qu'elle est : les blocs non
 * encore révélés sont vides et pleine hauteur, et les graphiques gardent la
 * largeur de l'écran alors que la feuille est plus étroite. La préparation
 * révèle tout, laisse une frame au navigateur pour appliquer la mise en page
 * papier, puis attend que les graphiques se soient remesurés avant d'ouvrir la
 * boîte d'impression.
 */
@Injectable()
export class ReportPrintService {
  private readonly document = inject(DOCUMENT);
  private readonly zone = inject(NgZone);

  /** Vrai le temps de la préparation, pour désactiver le bouton. */
  readonly preparing = signal(false);

  /**
   * Prépare puis imprime.
   *
   * @param scope Panneaux à imprimer. `'all'`, le cas du rapport complet,
   * déplie tous les panneaux de matière : à l'écran on n'en regarde qu'un,
   * mais le PDF doit tous les contenir, chacun sur ses propres pages.
   * `'current'` restreint le tirage à la matière consultée.
   *
   * C'est bien `'current'` qui pose une classe, et non l'inverse : imprimer
   * tout est le comportement de la feuille par défaut, de sorte qu'un tirage
   * déclenché par le navigateur (Ctrl+P, aperçu) ne perde jamais de matière.
   */
  async print(scope: 'current' | 'all' = 'current'): Promise<void> {
    if (this.preparing()) return;

    this.preparing.set(true);
    const root = this.document.documentElement;

    root.classList.add('print-prep');
    if (scope === 'current') root.classList.add('print-current-only');

    try {
      await this.settle();
      this.zone.runOutsideAngular(() => this.document.defaultView?.print());
    } finally {
      root.classList.remove('print-prep', 'print-current-only');
      this.preparing.set(false);
    }
  }

  /**
   * Laisse la page atteindre son état imprimable.
   *
   * Une frame pour que `print-prep` soit appliqué et que les `ResizeObserver`
   * des graphiques reçoivent leur nouvelle largeur, puis un court délai pour
   * que le redessin qui en découle soit peint. Les 120 ms viennent du document
   * d'origine ; en dessous, les graphiques larges sortaient rognés.
   */
  private settle(): Promise<void> {
    revealAll();

    return new Promise((resolve) =>
      this.zone.runOutsideAngular(() =>
        requestAnimationFrame(() => setTimeout(resolve, 120)),
      ),
    );
  }
}
